import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { PermPostOfferOfferModel } from 'src/app/root-store/perm-post-offer-workflow-store/models/perm-post-offer-offer.model';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import {
    AbstractControl,
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
    FormsModule,
    ReactiveFormsModule
} from '@angular/forms';
import { Observable, filter, merge, take } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SubmittalRemovalReason } from 'src/app/accountmanagers/submittals/model/submittal-removal-reasons.model';
import {
    PermanentJobOfferStatusEnum,
    PostOfferInformation
} from 'src/app/root-store/perm-post-offer-workflow-store/models/perm-post-offer-information.model';
import {
    cancelPostOfferModal,
    updateOfferInfoAndSubmittalStatus
} from 'src/app/root-store/perm-post-offer-workflow-store/actions';
import { PermPostOfferUpdateOfferPayload } from 'src/app/root-store/perm-post-offer-workflow-store/models/perm-post-offer-update-offer-payload.model';
import { checkChar, formatAndAssignCurrencyValue } from '../utils';
import { AmSubmittalStatusUpdateCancelButtonComponent } from '../../components/am-submittal-status-update-cancel-button/am-submittal-status-update-cancel-button.component';
import { CurrencyPipe, NgIf, NgFor, AsyncPipe } from '@angular/common';
import * as permPostOfferCandidateWorkflowSelectors from '../../../../root-store/perm-post-offer-workflow-store/perm-post-offer-candidate-workflow.selectors';
import { MatButtonModule } from '@angular/material/button';
import { TextFieldModule } from '@angular/cdk/text-field';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';
import { SendToClientInfoCardComponent } from '../send-to-client-modal/send-to-client-info-card/send-to-client-info-card.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';

@UntilDestroy()
@Component({
    selector: 'nova-am-submittal-status-update-perm-offer-modal',
    templateUrl: './perm-offer-modal.component.html',
    styleUrls: ['./perm-offer-modal.component.scss'],
    providers: [CurrencyPipe],
    standalone: true,
    imports: [
        MatDialogModule,
        NgIf,
        MatProgressBarModule,
        FormsModule,
        ReactiveFormsModule,
        SendToClientInfoCardComponent,
        MatRadioModule,
        NgFor,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatSelectModule,
        MatOptionModule,
        TextFieldModule,
        MatButtonModule,
        AmSubmittalStatusUpdateCancelButtonComponent,
        AsyncPipe
    ]
})
export class PermOfferModalComponent implements OnInit {
    // Constants
    readonly labelFlex = '25';
    readonly dataFlex = '75';
    readonly maxNoteLength = 1800;
    readonly permanentJobOfferStatusOptions = [
        {
            status: PermanentJobOfferStatusEnum.Accepted,
            value: 'Accepted'
        },
        {
            status: PermanentJobOfferStatusEnum.Declined,
            value: 'Declined'
        },
        {
            status: PermanentJobOfferStatusEnum.Pending,
            value: 'Pending'
        }
    ];

    @ViewChild('cancelButton') cancelButton: AmSubmittalStatusUpdateCancelButtonComponent;

    // Form Controls
    offerStatusControl: FormControl;
    offerDateControl: FormControl;
    billingAmountControl: FormControl;
    startDateControl: FormControl;
    removalReasonControl: FormControl;
    liveListNoteControl: FormControl;

    // Form Group
    offerForm: FormGroup;

    // Observables
    facilityPermBillingInformation$: Observable<string>;
    offerDeclinedRemovalReasons$: Observable<SubmittalRemovalReason[]>;

    // Data
    viewModel: PermPostOfferOfferModel;
    postOfferData: PostOfferInformation;

    // State
    showRemovalView = false;

    // Form Errors
    billingAmountError: string;
    startDateError: string;
    offerDateError: string;

    // Live List Note
    noteLength = 0;
    noteError: boolean;

    constructor(
        @Inject(MAT_DIALOG_DATA) data: PermPostOfferOfferModel,
        private readonly store: Store,
        private readonly _formBuilder: FormBuilder,
        private readonly _currencyPipe: CurrencyPipe
    ) {
        this.viewModel = data;
        this.buildFormControls();
        this.buildForm();
    }

    ngOnInit(): void {
        this.selectPostOfferModalIsLoading();
        this.initializeForm();
        this.selectFacilityPermBillingInformation();
        this.selectDefaultBillingAmount();
        this.selectPostOfferData();
    }

    initializeForm(): void {
        if (this.viewModel.displayOfferStatusSelection) {
            this.offerDeclinedRemovalReasons$ = this.store.select(
                permPostOfferCandidateWorkflowSelectors.selectOfferDeclinedRemovalReasons
            );
        }

        this.startFormControlListeners();
    }

    buildFormControls(): void {
        const today = new Date();
        this.offerDateControl = this._formBuilder.control(today, [Validators.required]);
        this.billingAmountControl = this._formBuilder.control(null, [Validators.required, Validators.min(0)]);
        this.startDateControl = this._formBuilder.control(null, [Validators.required, this.startDateValidator]);
        this.liveListNoteControl = this._formBuilder.control('', [Validators.maxLength(this.maxNoteLength)]);

        if (this.viewModel.displayOfferStatusSelection) {
            this.offerStatusControl = this._formBuilder.control(PermanentJobOfferStatusEnum.Pending, [
                Validators.required
            ]);
            this.removalReasonControl = this._formBuilder.control(null, [Validators.required]);
        }
    }

    buildForm(): void {
        const formControls: Record<string, AbstractControl> = {
            offerDate: this.offerDateControl,
            billingAmount: this.billingAmountControl,
            startDate: this.startDateControl,
            liveListNote: this.liveListNoteControl
        };

        if (this.viewModel.displayOfferStatusSelection) {
            formControls.offerStatus = this.offerStatusControl;
        }

        this.offerForm = this._formBuilder.group(formControls);
    }

    buildFormForDeclinedStatus(): void {
        this.offerForm = this._formBuilder.group({
            offerDate: this.offerDateControl,
            removalReason: this.removalReasonControl,
            offerStatus: this.offerStatusControl,
            liveListNote: this.liveListNoteControl
        });
    }

    checkErrors(control: FormControl): string {
        if (control.hasError('required')) {
            return 'You must enter a value';
        } else if (control.hasError('min')) {
            return 'Cannot be less than zero';
        } else if (control.hasError('invalidStartDate')) {
            return 'Cannot be before offer date';
        } else {
            return '';
        }
    }

    startDateValidator = (control: FormControl): { invalidStartDate: boolean } => {
        const offerDate = this.offerDateControl.value as Date;
        const startDate = control.value as Date;

        if (startDate && offerDate && this.zeroDate(startDate) < this.zeroDate(offerDate)) {
            return { invalidStartDate: true };
        }
        return null;
    };

    checkChar(e: KeyboardEvent): boolean {
        return checkChar(e);
    }

    onBlur(event: Event): boolean {
        return formatAndAssignCurrencyValue(event, this._currencyPipe);
    }

    onSubmit(): void {
        if (this.offerForm.valid) {
            const offerData: PermPostOfferUpdateOfferPayload = {
                offerDate: this.zeroDate(this.offerDateControl.value),
                liveListNote: this.liveListNoteControl.value === '' ? null : (this.liveListNoteControl.value as string)
            };

            if (
                this.viewModel.displayOfferStatusSelection &&
                this.offerStatusControl?.value === PermanentJobOfferStatusEnum.Declined
            ) {
                offerData.jobOfferStatus = PermanentJobOfferStatusEnum.Declined;
                offerData.removalReason = this.removalReasonControl.value as SubmittalRemovalReason;
            } else {
                offerData.billingAmount = this.billingAmountControl.value as number;
                offerData.jobOfferStatus = this.offerStatusControl
                    ? (this.offerStatusControl.value as PermanentJobOfferStatusEnum)
                    : this.postOfferData.jobOfferStatus;
                offerData.startDate = this.zeroDate(this.startDateControl.value);
            }

            this.store.dispatch(updateOfferInfoAndSubmittalStatus(offerData));
        }
    }

    zeroDate(date: Date): Date {
        date.setHours(0);
        date.setMinutes(0);
        date.setSeconds(0);
        date.setMilliseconds(0);

        return date;
    }

    closeDialog(): void {
        this.store.dispatch(cancelPostOfferModal());
    }

    startFormControlListeners(): void {
        this.listenToOfferStatusChanges();

        this.listenToLiveListNoteChanges();

        this.listenToOfferDateChanges();

        this.listenToBillingAmountChanges();

        this.listenToStartDateChanges();
    }

    listenToOfferStatusChanges(): void {
        if (this.offerStatusControl === undefined) {
            return;
        }

        this.offerStatusControl.valueChanges.pipe(untilDestroyed(this)).subscribe((status) => {
            if (status === PermanentJobOfferStatusEnum.Declined && this.showRemovalView === false) {
                this.buildFormForDeclinedStatus();
                this.showRemovalView = true;
            } else if (status !== PermanentJobOfferStatusEnum.Declined && this.showRemovalView === true) {
                this.buildForm();
                this.showRemovalView = false;
            }
        });
    }

    listenToLiveListNoteChanges(): void {
        this.liveListNoteControl.valueChanges.pipe(untilDestroyed(this)).subscribe((change: string | undefined) => {
            this.noteLength = change?.length ?? 0;
            this.noteError = this.noteLength > this.maxNoteLength;
        });
    }

    listenToOfferDateChanges(): void {
        merge(this.offerDateControl.statusChanges, this.offerDateControl.valueChanges)
            .pipe(untilDestroyed(this))
            .subscribe(() => (this.offerDateError = this.checkErrors(this.offerDateControl)));
    }

    listenToBillingAmountChanges(): void {
        merge(this.billingAmountControl.statusChanges, this.billingAmountControl.valueChanges)
            .pipe(untilDestroyed(this))
            .subscribe(() => (this.billingAmountError = this.checkErrors(this.billingAmountControl)));
    }

    listenToStartDateChanges(): void {
        merge(this.startDateControl.statusChanges, this.startDateControl.valueChanges)
            .pipe(untilDestroyed(this))
            .subscribe(() => (this.startDateError = this.checkErrors(this.startDateControl)));
    }

    private selectPostOfferModalIsLoading() {
        let allDataLoaded = false;
        this.store
            .select(permPostOfferCandidateWorkflowSelectors.selectPostOfferModalIsLoading)
            .pipe(
                filter((r) => r !== undefined),
                untilDestroyed(this)
            )
            .subscribe((loadingResult) => {
                if (!loadingResult.isLoading) {
                    allDataLoaded = true;
                    this.cancelButton?.enable();
                    this.offerForm?.enable();
                } else {
                    if (allDataLoaded) {
                        this.cancelButton?.disable();
                    }
                    this.offerForm?.disable();
                }
            });
    }

    private selectDefaultBillingAmount() {
        this.store
            .select(permPostOfferCandidateWorkflowSelectors.selectDefaultBillingAmount)
            .pipe(
                filter((r) => r !== undefined),
                untilDestroyed(this)
            )
            .subscribe((jobBillRate) => {
                this.billingAmountControl?.setValue(jobBillRate);
            });
    }

    private selectPostOfferData() {
        this.store
            .select(permPostOfferCandidateWorkflowSelectors.selectPostOfferData)
            .pipe(
                filter((r) => r !== undefined),
                take(1),
                untilDestroyed(this)
            )
            .subscribe((postOfferData) => {
                this.postOfferData = postOfferData;

                if (postOfferData.offerDate) {
                    this.offerDateControl?.setValue(postOfferData.offerDate);
                }

                if (postOfferData.startDate) {
                    this.startDateControl?.setValue(postOfferData.startDate);
                }

                if (postOfferData.jobOfferStatus) {
                    this.offerStatusControl?.setValue(postOfferData.jobOfferStatus);
                }
            });
    }

    private selectFacilityPermBillingInformation() {
        const selectFacilityPermBillingInformation$ = this.store.select(
            permPostOfferCandidateWorkflowSelectors.selectFacilityPermBillingInformation
        );
        this.facilityPermBillingInformation$ = selectFacilityPermBillingInformation$.pipe(
            filter((r) => r !== undefined),
            take(1),
            untilDestroyed(this)
        );
    }
}
